/**
 * Admin Entitlement Overrides - P2 Feature
 * 
 * Emergency tools for:
 * - Comp: Grant Elite/Elite+ for X days
 * - Extend: Add X days to existing tier
 * - Restore: Reactivate expired tier
 * - Revoke: Drop to free
 * 
 * Everything is logged to entitlement_ledger + admin_audit.
 */

import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../App";
import { ADMIN_ROUTES } from "../../config/adminRoutes";
import { ArrowLeft, AlertTriangle, Shield, Clock, Gift, Trash2 } from "lucide-react";

const API = window.location.origin;

function Pill({ children, color = "slate" }) {
  const colorClasses = {
    slate: "border-slate-700 bg-slate-800/50 text-slate-300",
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    rose: "border-rose-500/30 bg-rose-500/10 text-rose-400",
    purple: "border-purple-500/30 bg-purple-500/10 text-purple-400",
  };
  return (
    <span className={`text-xs px-2 py-1 rounded-full border ${colorClasses[color] || colorClasses.slate}`}>
      {children}
    </span>
  );
}

function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return "—";
  }
}

export default function AdminEntitlementsOverride() {
  const { userId } = useParams();
  const { token } = useContext(AuthContext);
  const nav = useNavigate();

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [action, setAction] = useState("comp");
  const [tier, setTier] = useState("elite");
  const [days, setDays] = useState(30);
  const [toast, setToast] = useState(null);

  async function load() {
    setBusy(true);
    setToast(null);
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/entitlements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "Failed to load entitlements");
      setData(json);
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    
  }, [userId]);

  async function submit() {
    if (!note.trim()) {
      setToast({ type: "error", msg: "Please provide a note explaining why you're doing this" });
      return;
    }

    setBusy(true);
    setToast(null);
    try {
      const body = {
        action,
        tier: (action === "extend" || action === "revoke") ? undefined : tier,
        days: (action === "comp" || action === "extend") ? Number(days) : undefined,
        note,
        request_id: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()),
      };

      const res = await fetch(`${API}/api/admin/users/${userId}/entitlements/override`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "Override failed");

      if (json.idempotent) {
        setToast({ type: "ok", msg: "Already processed (idempotent) ✅" });
      } else {
        setToast({ type: "ok", msg: `Override applied: ${action} → ${json.tier || 'free'} ✅` });
      }
      setNote("");
      await load();
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  const effectiveTier = data?.subscription_tier || "free";
  const tierColor = effectiveTier === "elite_plus" ? "purple" : effectiveTier === "elite" ? "emerald" : "slate";

  // Get action icon and color
  const actionConfig = {
    comp: { icon: Gift, color: "emerald", label: "Comp (grant for X days)" },
    extend: { icon: Clock, color: "amber", label: "Extend (add X days)" },
    restore: { icon: Shield, color: "purple", label: "Restore (reactivate tier)" },
    revoke: { icon: Trash2, color: "rose", label: "Revoke (set to free)" },
  };

  const ActionIcon = actionConfig[action]?.icon || Gift;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <button
              onClick={() => nav(ADMIN_ROUTES.USER_360(userId))}
              className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to User 360</span>
            </button>
            <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
              Entitlement Overrides
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Emergency tools. Everything is logged. Don't be spicy.
            </p>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
            toast.type === "ok" 
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" 
              : "border-rose-500/30 bg-rose-500/10 text-rose-400"
          }`}>
            {toast.msg}
          </div>
        )}

        {/* Current State Card */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Current Entitlement State</h2>
          
          {data ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Pill color={tierColor}>tier = {effectiveTier}</Pill>
                <Pill color={data?.is_elite ? "emerald" : "slate"}>is_elite = {String(data?.is_elite)}</Pill>
                <Pill color={data?.is_elite_plus ? "purple" : "slate"}>is_elite_plus = {String(data?.is_elite_plus)}</Pill>
                {data?.risk_hold && <Pill color="rose">⚠️ RISK HOLD</Pill>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-slate-500">User</div>
                  <div className="text-slate-300">{data?.email || data?.id}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Stripe Status</div>
                  <div className="text-slate-300">{data?.stripe_subscription_status || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Expires At</div>
                  <div className="text-slate-300">{fmtDate(data?.plan_expires_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Renews At</div>
                  <div className="text-slate-300">{fmtDate(data?.plan_renews_at)}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-slate-500">Loading...</div>
          )}
        </div>

        {/* Override Controls */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className={`p-2 rounded-lg bg-${actionConfig[action]?.color || 'slate'}-500/10`}>
              <ActionIcon className={`w-5 h-5 text-${actionConfig[action]?.color || 'slate'}-400`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Apply Override</h2>
              <p className="text-xs text-slate-500">Select action and configure parameters</p>
            </div>
          </div>

          <div className="grid gap-4">
            {/* Action Select */}
            <label className="text-sm">
              <div className="text-xs text-slate-500 mb-2">Action</div>
              <select 
                className="w-full rounded-xl border border-slate-700 bg-slate-800/50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none transition"
                value={action} 
                onChange={(e) => setAction(e.target.value)}
              >
                <option value="comp">🎁 Comp (grant tier for X days)</option>
                <option value="extend">⏰ Extend (add X days to existing)</option>
                <option value="restore">🛡️ Restore (reactivate expired tier)</option>
                <option value="revoke">🗑️ Revoke (drop to free)</option>
              </select>
            </label>

            {/* Tier Select (for comp/restore) */}
            {(action === "comp" || action === "restore") && (
              <label className="text-sm">
                <div className="text-xs text-slate-500 mb-2">Tier</div>
                <select 
                  className="w-full rounded-xl border border-slate-700 bg-slate-800/50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none transition"
                  value={tier} 
                  onChange={(e) => setTier(e.target.value)}
                >
                  <option value="elite">Elite</option>
                  <option value="elite_plus">Elite+</option>
                </select>
              </label>
            )}

            {/* Days Input (for comp/extend) */}
            {(action === "comp" || action === "extend") && (
              <label className="text-sm">
                <div className="text-xs text-slate-500 mb-2">Days (1-365)</div>
                <input
                  className="w-full rounded-xl border border-slate-700 bg-slate-800/50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none transition"
                  type="number"
                  min="1"
                  max="365"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                />
              </label>
            )}

            {/* Note (required) */}
            <label className="text-sm">
              <div className="text-xs text-slate-500 mb-2">Admin Note (required)</div>
              <textarea
                className="w-full rounded-xl border border-slate-700 bg-slate-800/50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none transition resize-none"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why are we doing this? (ex: comp for beta testing, refund goodwill, support issue...)"
              />
            </label>

            {/* Warning for revoke */}
            {action === "revoke" && (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-rose-400">
                  <strong>Warning:</strong> This will immediately drop the user to free tier and mark them as intentionally revoked.
                  Use only for fraud/abuse cases or explicit user request.
                </div>
              </div>
            )}

            {/* Submit */}
            <button
              disabled={busy || !note.trim()}
              onClick={submit}
              className={`w-full rounded-xl border px-4 py-3 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${
                action === "revoke"
                  ? "border-rose-500/30 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20"
                  : "border-purple-500/30 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20"
              }`}
            >
              {busy ? "Applying..." : `Apply ${action.charAt(0).toUpperCase() + action.slice(1)}`}
            </button>

            <div className="text-xs text-slate-500 text-center">
              Every override writes to <span className="font-mono">entitlement_ledger</span> + <span className="font-mono">admin_audit</span>.
              Undo by applying a counter-action.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
